/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod";
import { ClosedEnum } from "../types/enums.js";

/**
 * 返回格式。留空或为 'text' 时返回原始WHOIS文本，设为 'json' 时返回结构化JSON。
 */
export const GetNetworkWhoisFormat = {
  Text: "text",
  Json: "json",
} as const;
/**
 * 返回格式。留空或为 'text' 时返回原始WHOIS文本，设为 'json' 时返回结构化JSON。
 */
export type GetNetworkWhoisFormat = ClosedEnum<typeof GetNetworkWhoisFormat>;

export const GetNetworkWhoisFormat$zodSchema = z.enum([
  "text",
  "json",
]).describe(
  "返回格式。留空或为 'text' 时返回原始WHOIS文本，设为 'json' 时返回结构化JSON。",
);

export type GetNetworkWhoisRequest = {
  domain: string;
  format?: GetNetworkWhoisFormat | undefined;
};

export const GetNetworkWhoisRequest$zodSchema: z.ZodType<
  GetNetworkWhoisRequest
> = z.object({
  domain: z.string().describe("你需要查询WHOIS信息的域名。"),
  format: GetNetworkWhoisFormat$zodSchema.default("text"),
});

export type GetNetworkWhoisNotFoundDetails = {};

export const GetNetworkWhoisNotFoundDetails$zodSchema: z.ZodType<
  GetNetworkWhoisNotFoundDetails
> = z.object({});

/**
 * 查询失败或域名不存在。
 */
export type GetNetworkWhoisNotFoundResponseBody = {
  code?: string | undefined;
  details?: GetNetworkWhoisNotFoundDetails | undefined;
  message?: string | undefined;
};

export const GetNetworkWhoisNotFoundResponseBody$zodSchema: z.ZodType<
  GetNetworkWhoisNotFoundResponseBody
> = z.object({
  code: z.string().optional(),
  details: z.lazy(() => GetNetworkWhoisNotFoundDetails$zodSchema).optional(),
  message: z.string().optional(),
}).describe("查询失败或域名不存在。");

export type GetNetworkWhoisBadRequestDetails = {};

export const GetNetworkWhoisBadRequestDetails$zodSchema: z.ZodType<
  GetNetworkWhoisBadRequestDetails
> = z.object({});

/**
 * 请求参数无效。请检查 `domain` 参数是否提供且格式正确。
 */
export type GetNetworkWhoisBadRequestResponseBody = {
  code?: string | undefined;
  details?: GetNetworkWhoisBadRequestDetails | undefined;
  message?: string | undefined;
};

export const GetNetworkWhoisBadRequestResponseBody$zodSchema: z.ZodType<
  GetNetworkWhoisBadRequestResponseBody
> = z.object({
  code: z.string().optional(),
  details: z.lazy(() => GetNetworkWhoisBadRequestDetails$zodSchema).optional(),
  message: z.string().optional(),
}).describe("请求参数无效。请检查 `domain` 参数是否提供且格式正确。");

/**
 * ### 结构化WHOIS信息
 *
 * @remarks
 *
 * 返回经过解析的JSON对象，包含以下主要部分：
 *
 * - **域名信息**: 包含域名ID、注册状态、DNS服务器等
 * - **注册商信息**: 注册服务商的详细信息
 * - **注册人信息**: 域名所有者的相关信息（可能因隐私保护而部分隐藏）
 * - **重要日期**: 包括注册日期、更新日期和到期日期
 */
export type Whois = {};

export const Whois$zodSchema: z.ZodType<Whois> = z.object({}).describe(
  "### 结构化WHOIS信息\n\n返回经过解析的JSON对象，包含以下主要部分：\n\n- **域名信息**: 包含域名ID、注册状态、DNS服务器等\n- **注册商信息**: 注册服务商的详细信息\n- **注册人信息**: 域名所有者的相关信息（可能因隐私保护而部分隐藏）\n- **重要日期**: 包括注册日期、更新日期和到期日期",
);

/**
 * ### JSON格式响应
 *
 * @remarks
 * 当 `format=json` 时，`whois` 字段返回结构化的JSON对象。
 *
 * > [!NOTE]
 * > **注意**：返回的具体字段可能因域名注册局和隐私保护设置而异。某些敏感信息可能会被部分隐藏或标记为 `REDACTED FOR PRIVACY`。
 */
export type ResponseBody2 = { whois?: Whois | undefined };

export const ResponseBody2$zodSchema: z.ZodType<ResponseBody2> = z.object({
  whois: z.lazy(() => Whois$zodSchema).optional(),
}).describe(
  "### JSON格式响应\n当 `format=json` 时，`whois` 字段返回结构化的JSON对象。\n\n> [!NOTE]\n> **注意**：返回的具体字段可能因域名注册局和隐私保护设置而异。某些敏感信息可能会被部分隐藏或标记为 `REDACTED FOR PRIVACY`。",
);

/**
 * ### 文本格式响应
 *
 * @remarks
 * 当 `format=text` 或未指定时，`whois` 字段包含原始的WHOIS查询文本。这保留了最完整的信息，适合需要自行解析或展示原始数据的场景。
 */
export type ResponseBody1 = { whois?: string | undefined };

export const ResponseBody1$zodSchema: z.ZodType<ResponseBody1> = z.object({
  whois: z.string().optional(),
}).describe(
  "### 文本格式响应\n当 `format=text` 或未指定时，`whois` 字段包含原始的WHOIS查询文本。这保留了最完整的信息，适合需要自行解析或展示原始数据的场景。",
);

/**
 * 查询成功！根据 format 参数返回原始WHOIS文本或结构化JSON。
 */
export type GetNetworkWhoisResponseBody = ResponseBody1 | ResponseBody2;

export const GetNetworkWhoisResponseBody$zodSchema: z.ZodType<
  GetNetworkWhoisResponseBody
> = z.union([
  z.lazy(() => ResponseBody1$zodSchema),
  z.lazy(() => ResponseBody2$zodSchema),
]).describe("查询成功！根据 format 参数返回原始WHOIS文本或结构化JSON。");

export type GetNetworkWhoisResponse =
  | ResponseBody1
  | ResponseBody2
  | GetNetworkWhoisBadRequestResponseBody
  | GetNetworkWhoisNotFoundResponseBody;

export const GetNetworkWhoisResponse$zodSchema: z.ZodType<
  GetNetworkWhoisResponse
> = z.union([
  z.union([
    z.lazy(() => ResponseBody1$zodSchema),
    z.lazy(() => ResponseBody2$zodSchema),
  ]),
  z.lazy(() => GetNetworkWhoisBadRequestResponseBody$zodSchema),
  z.lazy(() => GetNetworkWhoisNotFoundResponseBody$zodSchema),
]);
